import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BalanceCard } from './BalanceCard';
import { ActionRow } from './ActionRow';
import { ReferralBanner } from './ReferralBanner';
import { AssetRow } from './AssetRow';
import { TransactionRow } from './TransactionRow';
import { TransactionDetailSheet } from './TransactionDetailSheet';
import { WalletSwitcher } from './WalletSwitcher';
import { CardSelector } from './CardSelector';
import { useNav } from '../../store/nav';
import { useWallets } from '../../store/wallets';
import { useCoinPrices } from '../../hooks/usePrices';
import { useSession } from '../../hooks/useSession';
import { useBalances, useTxs } from '../../hooks/useChain';
import { useOnrampOrders, useHistoryWithPurchases } from '../../hooks/useOnramp';
import { useQrPayments, useHistoryWithQr } from '../../hooks/useQrPay';
import { useCards } from '../../hooks/useCardsApi';
import { toDisplayCard } from '../../lib/cards';
import { LoadMore } from './LoadMore';
import { mockTransactions, mockCards } from '../../lib/mock';
import { fmtUsd, fmtDateHeading } from '../../lib/format';
import { portfolioChange24h } from '../../lib/portfolio-change';

export function WalletMainScreen() {
  const { t } = useTranslation();
  const push = useNav((s) => s.push);
  const setTab = useNav((s) => s.setTab);
  const wallets = useWallets((s) => s.wallets);
  // The selection is shared, so pressing Send / Buy / Top up acts on the wallet
  // the user is actually looking at.
  const activeWalletId = useWallets((st) => st.activeId) ?? wallets[0]?.id ?? '';
  const setActiveWalletId = useWallets((st) => st.setActive);
  const [cardPopover, setCardPopover] = useState(false);
  const [detailTx, setDetailTx] = useState<(typeof mockTransactions)[number] | null>(null);

  const { token } = useSession();
  // Real cards when signed in; mock list only for the browser/dev preview.
  const apiCards = useCards().data;
  const cards = token ? (apiCards ?? []).map(toDisplayCard) : mockCards;
  // The wallets store hydrates from the server one tick after the gate enters
  // the app; `wallet` may be undefined for that first render.
  const wallet = wallets.find((w) => w.id === activeWalletId) ?? wallets[0];
  // Overlay live prices (Binance → CoinGecko) on top of each coin; the coin's own
  // priceUsd/change24h stay as the baseline until live data arrives. (Hooks must
  // run every render — pass undefined/[] when there's no wallet yet.)
  const prices = useCoinPrices(wallet?.coins.map((c) => c.symbol) ?? []).data;
  // Real on-chain balances + transfers for the active address (real Telegram
  // only; dev/browser keeps the mock baseline).
  const balQ = useBalances(wallet?.address);
  const balances = balQ.data;
  // No local snapshot yet — show skeleton, never a fake $0.
  const balancesLoading = !!token && !!wallet?.address && balances === undefined;
  const { data: txs, hasNextPage, isFetchingNextPage, fetchNextPage } = useTxs(wallet?.address);
  // Fiat purchases belong in the same history as everything else.
  const purchases = useOnrampOrders().data;
  // QR payments are the same on-chain sends, relabelled with the store details.
  const qrPayments = useQrPayments().data;
  const withPurchases = useHistoryWithPurchases(token ? txs ?? [] : mockTransactions, purchases);
  const history = useHistoryWithQr(withPurchases, qrPayments);

  // Render a spinner until the primary wallet is present (after all hooks).
  if (!wallet) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface">
        <span className="size-10 animate-spin rounded-full border-[3px] border-border border-t-accent" />
      </div>
    );
  }

  const coins = wallet.coins.map((c) => {
    const live = prices?.[c.symbol];
    // Real on-chain balance overrides the (zeroed) baseline in a real client.
    // While loading with no cache, keep 0 but the UI shows skeletons instead.
    const balance = token
      ? (c.symbol === 'USDT' ? balances?.usdt : balances?.trx) ?? 0
      : c.balance;
    return { ...c, balance, ...(live ? { priceUsd: live.usd, change24h: live.change24h } : {}) };
  });
  const total = balancesLoading ? 0 : coins.reduce((s, c) => s + c.balance * c.priceUsd, 0);
  const assetsTotal = total;
  // Price P&L on what you held + net confirmed transfers over 24h (sends/
  // receives must move the pill — pure price-backout ignores them).
  const { changeUsd, changePct } = portfolioChange24h(coins, history);

  // Real transfers in a real client (empty list = no activity yet); mock in dev.
  // On-chain txs carry no USD — fill it from the (live) coin price.
  const priceOf = (sym: string) => coins.find((c) => c.symbol === sym)?.priceUsd ?? (sym === 'USDT' ? 1 : 0);
  const list = history.map((tx) => ({
    ...tx,
    amountUsd: tx.amountUsd || Math.abs(tx.amount) * priceOf(tx.coin),
  }));
  const byDay = list.reduce<Record<string, typeof list>>((acc, tx) => {
    const key = fmtDateHeading(tx.date);
    (acc[key] ??= []).push(tx);
    return acc;
  }, {});
  // Only show the chip once Banana has issued digits (skip pending shells).
  const chipCard = cards.find((c) => /^\d{4}$/.test(c.last4));

  return (
    <div className="min-h-full px-4 pb-40 pt-3">
      {/* wallet-name dropdown above the balance */}
      <div className="mb-3">
        <WalletSwitcher wallets={wallets} activeId={activeWalletId} onSelect={setActiveWalletId} />
      </div>

      {/* balance card + anchored card selector popover */}
      <div className="relative">
        <BalanceCard
          balanceUsd={total}
          changePct={changePct}
          changeUsd={changeUsd}
          loading={balancesLoading}
          cardLast4={chipCard?.last4}
          onSelectCard={() => setCardPopover((v) => !v)}
        />
        {cardPopover && (
          <CardSelector
            cards={cards}
            onPick={() => {
              setCardPopover(false);
              setTab('cards');
            }}
            onAdd={() => {
              setCardPopover(false);
              setTab('cards');
            }}
            onClose={() => setCardPopover(false)}
          />
        )}
      </div>

      <div className="mt-4">
        <ActionRow />
      </div>

      <div className="mt-4">
        <ReferralBanner
          onReferral={() => push({ name: 'referral' })}
          onCards={() => setTab('cards')}
        />
      </div>

      <div className="mt-6 flex items-center justify-between px-1">
        <h2 className="text-[15px] font-semibold text-muted">{t('wallet.assets')}</h2>
        <span className="text-[15px] font-semibold text-muted">
          {balancesLoading ? '···' : fmtUsd(assetsTotal)}
        </span>
      </div>
      <div className="mt-2 divide-y divide-border overflow-hidden rounded-card bg-card shadow-card">
        {coins.map((c) => (
          <AssetRow
            key={c.symbol}
            coin={c}
            loading={balancesLoading}
            onClick={() => push({ name: 'coin', coin: c.symbol })}
          />
        ))}
      </div>

      {Object.entries(byDay).map(([day, txs]) => (
        <section key={day} className="mt-6">
          <h3 className="px-1 text-[15px] font-semibold text-muted">{day}</h3>
          <div className="mt-2 divide-y divide-border overflow-hidden rounded-card bg-card shadow-card">
            {txs.map((tx) => (
              <TransactionRow
                key={tx.id}
                tx={tx}
                {...(tx.type === 'qr' ? { badge: tx.merchant ?? null } : {})}
                onClick={() => setDetailTx(tx)}
              />
            ))}
          </div>
        </section>
      ))}

      <LoadMore hasMore={!!hasNextPage} loading={isFetchingNextPage} onLoadMore={fetchNextPage} />

      {detailTx && <TransactionDetailSheet tx={detailTx} onClose={() => setDetailTx(null)} />}
    </div>
  );
}
